import { styled } from "@mui/material";

export const StyledHeader = {
  Root: styled("header", {
    shouldForwardProp: (prop) => prop !== "scrolled",
  })(({ theme, scrolled }) => ({
    position: "fixed",
    inset: "0 0 auto 0",
    zIndex: 100,
    height: theme.custom.navHeight,
    display: "flex",
    alignItems: "center",
    transition: "background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
    borderBottom: "1px solid transparent",
    ...(scrolled && {
      background: "rgba(255, 255, 255, 0.82)",
      backdropFilter: "saturate(180%) blur(16px)",
      borderBottomColor: theme.custom.colors.border,
      boxShadow: "0 6px 24px -18px rgba(16, 26, 61, 0.35)",
    }),
  })),

  Inner: styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 24,
    width: "100%",
  })),

  Links: styled("nav")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginInline: "auto",
    "@media (max-width: 980px)": { display: "none" },
  })),

  Link: styled("a")(({ theme }) => ({
    position: "relative",
    fontSize: "0.95rem",
    fontWeight: 500,
    color: theme.custom.colors.textSoft,
    padding: "9px 14px",
    borderRadius: theme.custom.radius.full,
    transition: "color 0.2s ease, background 0.2s ease",
    "&:hover": {
      color: theme.palette.text.primary,
      background: "rgba(23, 79, 155, 0.07)",
    },
  })),

  Actions: styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: 14,
  })),

  Login: styled("a")(({ theme }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    fontWeight: 600,
    fontSize: "0.9rem",
    color: theme.custom.colors.indigo600,
    padding: "8px 16px",
    borderRadius: theme.custom.radius.full,
    border: `1px solid ${theme.custom.colors.border}`,
    background: "rgba(255, 255, 255, 0.6)",
    transition: "color 0.2s ease, background 0.2s ease, border-color 0.2s ease, transform 0.2s ease",
    "& svg": { fontSize: "1rem", opacity: 0.85 },
    "&:hover": {
      color: "#fff",
      background: theme.custom.gradient.brand,
      borderColor: "transparent",
      transform: "translateY(-1px)",
    },
    "&:hover svg": { opacity: 1 },
    "@media (max-width: 980px)": { display: "none" },
  })),

  Cta: styled("div")(({ theme }) => ({
    "@media (max-width: 980px)": { display: "none" },
  })),

  Burger: styled("button")(({ theme }) => ({
    display: "none",
    border: "none",
    background: "none",
    cursor: "pointer",
    fontSize: "1.7rem",
    color: theme.palette.text.primary,
    padding: 4,
    "& svg": { fontSize: "1.9rem" },
    "@media (max-width: 980px)": { display: "inline-flex" },
  })),

  Drawer: styled("div", {
    shouldForwardProp: (prop) => prop !== "open",
  })(({ theme, open }) => ({
    position: "fixed",
    inset: `${theme.custom.navHeight}px 0 0 0`,
    zIndex: 200,
    // Fondo totalmente opaco: evita que el contenido del sitio se transparente
    // detrás del menú. (Antes usaba rgba(...,0.98) + backdrop-filter, que al
    // anidarse con el backdrop-filter del header scrolleado dejaba de pintar
    // en varios navegadores móviles y el contenido quedaba superpuesto.)
    background: theme.palette.background.default,
    overflowY: "auto",
    padding: "32px clamp(20px, 6vw, 40px) 48px",
    display: "flex",
    flexDirection: "column",
    gap: 26,
    transform: open ? "translateX(0)" : "translateX(100%)",
    visibility: open ? "visible" : "hidden",
    transition: "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
    "@media (min-width: 981px)": { display: "none" },
  })),

  DrawerLinks: styled("nav")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: 4,
    "& a": {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontFamily: theme.custom.fonts.display,
      fontWeight: 600,
      fontSize: "1.35rem",
      padding: "14px 0",
      borderBottom: `1px solid ${theme.custom.colors.border}`,
      color: theme.palette.text.primary,
    },
    "& a svg": { fontSize: "1.15rem", color: theme.custom.colors.indigo600 },
  })),
};
